import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { 
  Firestore,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { onIdTokenChanged, User } from 'firebase/auth';
import { useFirebase } from './FirebaseContext';
import logger from '../utils/logger';

// Role hierarchy: admin > team_admin > general_user
export type UserRole = 'admin' | 'team_admin' | 'general_user';

export interface UserPermissions {
  id: string;
  email: string;
  role: UserRole;
  roles: string[]; // Array of specific roles like ['admin'], ['admin_org_123'], ['admin_team_456']
  assignedBy: string;
  assignedAt: Date;
  isActive: boolean;
  verifiedSiteAdmin?: boolean;
  // Additional role-specific data
  teamIds?: string[]; // For team_admin role
  clubIds?: string[]; // For team_admin role
}

interface RoleContextType {
  userRole: UserRole | null;
  userPermissions: UserPermissions | null;
  loading: boolean;
  hasRole: (requiredRole: UserRole) => boolean;
  hasMinimumRole: (minimumRole: UserRole) => boolean;
  // New role array functions
  isWebsiteAdmin: () => boolean;
  canManageOrganization: (orgId: string) => boolean;
  canManageTeam: (teamId: string) => boolean;
  getManagedOrganizations: () => string[];
  getManagedTeams: () => string[];
  refreshUserRole: () => Promise<void>;
  assignRole: (email: string, role: UserRole, assignedBy: string, entityId?: string) => Promise<boolean>;
  assignSpecificRole: (email: string, roleString: string, assignedBy: string) => Promise<boolean>;
  revokeRole: (email: string) => Promise<boolean>;
  revokeSpecificRole: (email: string, roleString: string) => Promise<boolean>;
  getAllRoles: () => Promise<UserPermissions[]>;
}

const RoleContext = createContext<RoleContextType | null>(null);

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

interface RoleProviderProps {
  children: React.ReactNode;
  db: Firestore;
  user?: User | null; // Keep as optional for backward compatibility
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ children, db, user: propUser }) => {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const roleGeneration = useRef(0);
  
  // Get user and auth from Firebase context if available, otherwise use prop
  let firebaseUser: User | null = null;
  let auth: any = null;
  try {
    const { user: fbUser, auth: fbAuth } = useFirebase();
    firebaseUser = fbUser;
    auth = fbAuth;
    logger.log('🔍 RoleProvider: Got user from Firebase context:', fbUser?.email || 'null');
  } catch (error) {
    logger.log('🔍 RoleProvider: Firebase context not available, using prop user:', propUser?.email || 'null');
    firebaseUser = propUser || null;
  }
  
  const user = firebaseUser || propUser;
  logger.log('🔍 RoleProvider: Final user for role context:', user?.email || 'null');

  // Role hierarchy for permission checking
  const roleHierarchy: Record<UserRole, number> = {
    'admin': 3,
    'team_admin': 2,
    'general_user': 1
  };

  const fetchUserRole = async (userId: string, email: string): Promise<UserPermissions | null> => {
    try {
      if (!user || user.uid !== userId) return null;
      const token = await user.getIdTokenResult();
      const verifiedSiteAdmin = token.claims.admin === true;
      logger.log('🔍 fetchUserRole: Looking up userId:', userId, 'email:', email);
      
      // Read role directly from users collection instead of userRoles
      const userDoc = doc(db, 'users', userId);
      const docSnap = await getDoc(userDoc);
      
      logger.log('🔍 fetchUserRole: User document exists:', docSnap.exists());
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        logger.log('🔍 fetchUserRole: User document data:', data);
        
        // Handle role array migration
        let userRoles: string[] = [];
        if (Array.isArray(data.role)) {
          userRoles = data.role;
        } else if (data.role === 'admin') {
          userRoles = ['admin'];
        } else if (data.role === 'team_admin') {
          userRoles = ['team_admin']; // Will be migrated to specific team roles later
        } else {
          userRoles = ['user'];
        }
        
        // Determine highest role for backward compatibility
        let mappedRole: UserRole = 'general_user';
        if (verifiedSiteAdmin) {
          mappedRole = 'admin';
        } else if (userRoles.some(role => role.startsWith('admin_team_') || role.startsWith('admin_org_') || role === 'team_admin')) {
          mappedRole = 'team_admin';
        }
        
        const permissions: UserPermissions = {
          id: userId,
          email: data.email || email,
          role: mappedRole,
          roles: userRoles,
          assignedBy: 'system',
          assignedAt: new Date(),
          isActive: true,
          verifiedSiteAdmin,
          teamIds: [],
          clubIds: []
        };
        
        logger.log('🔍 fetchUserRole: Returning permissions from users collection:', permissions);
        return permissions;
      }
      
      logger.log('❌ fetchUserRole: No user document found for userId:', userId);
      return null;
    } catch (error) {
      logger.error('❌ fetchUserRole: Error fetching user role:', error);
      return null;
    }
  };

  const refreshUserRole = async (): Promise<void> => {
    const generation = ++roleGeneration.current;
    logger.log('🔍 RoleContext: refreshUserRole called', { user: user?.email, uid: user?.uid });
    
    if (!user?.email) {
      logger.log('❌ RoleContext: No user email, clearing roles');
      setUserRole(null);
      setUserPermissions(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setUserRole(null);
    setUserPermissions(null);
    try {
      logger.log('🔍 RoleContext: Fetching role for user:', user.email);
      const permissions = await fetchUserRole(user.uid, user.email);
      if (generation !== roleGeneration.current) return;
      logger.log('🔍 RoleContext: Fetched permissions:', permissions);
      
      if (permissions && permissions.isActive) {
        logger.log('✅ RoleContext: Setting role:', permissions.role);
        setUserRole(permissions.role);
        setUserPermissions(permissions);
      } else {
        logger.log('❌ RoleContext: No active permissions found');
        setUserRole(null);
        setUserPermissions(null);
      }
    } catch (error) {
      if (generation !== roleGeneration.current) return;
      logger.error('❌ RoleContext: Error refreshing user role:', error);
      setUserRole(null);
      setUserPermissions(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    const unsubscribe = auth
      ? onIdTokenChanged(auth, () => { void refreshUserRole(); })
      : undefined;
    if (!auth) void refreshUserRole();
    return () => {
      ++roleGeneration.current;
      unsubscribe?.();
    };
  }, [user, auth]);

  const hasRole = (requiredRole: UserRole): boolean => {
    if (requiredRole === 'admin') return isWebsiteAdmin();
    if (!userRole) return false;
    return userRole === requiredRole;
  };

  const hasMinimumRole = (minimumRole: UserRole): boolean => {
    if (minimumRole === 'admin') return isWebsiteAdmin();
    if (!userRole) return false;
    return roleHierarchy[userRole] >= roleHierarchy[minimumRole];
  };

  // New role array helper functions
  const isWebsiteAdmin = (): boolean => {
    return !loading && userPermissions?.id === user?.uid
      && userPermissions?.verifiedSiteAdmin === true;
  };

  const canManageOrganization = (orgId: string): boolean => {
    return isWebsiteAdmin();
  };

  const canManageTeam = (teamId: string): boolean => {
    return isWebsiteAdmin();
  };

  const getManagedOrganizations = (): string[] => {
    if (!userPermissions?.roles) return [];
    return userPermissions.roles
      .filter(role => role.startsWith('admin_org_'))
      .map(role => role.replace('admin_org_', ''));
  };

  const getManagedTeams = (): string[] => {
    if (!userPermissions?.roles) return [];
    return userPermissions.roles
      .filter(role => role.startsWith('admin_team_'))
      .map(role => role.replace('admin_team_', ''));
  };

  const assignRole = async (email: string, role: UserRole, assignedBy: string, entityId?: string): Promise<boolean> => {
    try {
      // First, find the user by email in the users collection
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      let userId: string | null = null;
      querySnapshot.forEach((doc) => {
        userId = doc.id;
      });

      if (!userId) {
        logger.error('User with email not found:', email);
        return false;
      }

      // Get current user document to read existing roles
      const userDoc = doc(db, 'users', userId);
      const docSnap = await getDoc(userDoc);
      let currentRoles: string[] = ['user'];
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (Array.isArray(data.role)) {
          currentRoles = data.role;
        } else if (data.role === 'admin') {
          currentRoles = ['admin'];
        } else if (data.role === 'team_admin') {
          currentRoles = ['team_admin'];
        }
      }

      // Build new role string based on role type
      let newRoleString = '';
      if (role === 'admin') {
        newRoleString = 'admin';
      } else if (role === 'team_admin' && entityId) {
        // Determine if entityId is for org or team based on prefix or collection check
        newRoleString = entityId.startsWith('org_') ? `admin_${entityId}` : `admin_team_${entityId}`;
      }

      // Add new role if not already present
      if (newRoleString && !currentRoles.includes(newRoleString)) {
        // Remove 'user' role if we're adding admin roles
        if (newRoleString !== 'user') {
          currentRoles = currentRoles.filter(r => r !== 'user');
        }
        currentRoles.push(newRoleString);
      }

      // Update role array in users collection
      await setDoc(userDoc, { role: currentRoles }, { merge: true });
      
      logger.log(`Role ${role} assigned to ${email}`, { newRoles: currentRoles });
      return true;
    } catch (error) {
      logger.error('Error assigning role:', error);
      return false;
    }
  };

  const assignSpecificRole = async (email: string, roleString: string, assignedBy: string): Promise<boolean> => {
    try {
      logger.log(`🔍 Assigning role ${roleString} to ${email}`);
      
      // First, try to find the user by email in the users collection
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      let userId: string | null = null;
      querySnapshot.forEach((doc) => {
        userId = doc.id;
      });

      // If user not found by email, we need to get their UID from teamMembers collection
      if (!userId) {
        logger.log(`⚠️ User with email ${email} not found in users collection, checking teamMembers...`);
        
        // Look for the user in teamMembers to get their userId (which should be the Firebase Auth UID)
        const teamMembersRef = collection(db, 'teamMembers');
        const teamMemberQuery = query(teamMembersRef, where('userEmail', '==', email.toLowerCase()));
        const teamMemberSnapshot = await getDocs(teamMemberQuery);
        
        if (!teamMemberSnapshot.empty) {
          userId = teamMemberSnapshot.docs[0].data().userId;
          logger.log(`✅ Found userId ${userId} from teamMembers collection`);
        } else {
          logger.error(`❌ User with email ${email} not found in teamMembers either`);
          return false;
        }
      }

      // Ensure we have a valid userId
      if (!userId) {
        logger.error('❌ Could not determine userId for role assignment');
        return false;
      }

      // Get current user document to read existing roles
      const userDoc = doc(db, 'users', userId);
      const docSnap = await getDoc(userDoc);
      let currentRoles: string[] = ['user'];
      let userData: any = {};
      
      if (docSnap.exists()) {
        userData = docSnap.data();
        if (Array.isArray(userData.role)) {
          currentRoles = userData.role;
        } else if (userData.role === 'admin') {
          currentRoles = ['admin'];
        } else if (userData.role === 'team_admin') {
          currentRoles = ['team_admin'];
        }
        logger.log(`📋 Existing user data:`, userData);
      } else {
        logger.log(`🆕 User document doesn't exist, will create with role assignment`);
        // Create basic user data structure
        userData = {
          email: email.toLowerCase(),
          displayName: email.split('@')[0],
          totalJuggles: 0,
          longestSession: 0,
          bestSessionCount: 0,
          bestRate: 0,
          joinDate: new Date(),
          isPremium: false,
          subscriptionTier: 'free'
        };
      }

      // Add new role if not already present
      if (!currentRoles.includes(roleString)) {
        // Remove 'user' role if we're adding admin roles
        if (roleString !== 'user') {
          currentRoles = currentRoles.filter(r => r !== 'user');
        }
        currentRoles.push(roleString);
      }

      // Update user document with role array
      await setDoc(userDoc, { 
        ...userData,
        role: currentRoles 
      }, { merge: true });
      
      logger.log(`✅ Role ${roleString} assigned to ${email}`, { 
        userId, 
        newRoles: currentRoles,
        created: !docSnap.exists()
      });
      return true;
    } catch (error) {
      logger.error('Error assigning specific role:', error);
      return false;
    }
  };

  const revokeRole = async (email: string): Promise<boolean> => {
    try {
      // Find user by email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      let userId: string | null = null;
      querySnapshot.forEach((doc) => {
        userId = doc.id;
      });

      if (!userId) {
        logger.error('User with email not found:', email);
        return false;
      }

      // Update role in users collection (set to ['user'] to revoke all admin roles)
      const userDoc = doc(db, 'users', userId);
      await setDoc(userDoc, { role: ['user'] }, { merge: true });
      
      logger.log(`All roles revoked for ${email}`);
      return true;
    } catch (error) {
      logger.error('Error revoking role:', error);
      return false;
    }
  };

  const revokeSpecificRole = async (email: string, roleString: string): Promise<boolean> => {
    try {
      // Find user by email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      let userId: string | null = null;
      querySnapshot.forEach((doc) => {
        userId = doc.id;
      });

      if (!userId) {
        logger.error('User with email not found:', email);
        return false;
      }

      // Get current user document to read existing roles
      const userDoc = doc(db, 'users', userId);
      const docSnap = await getDoc(userDoc);
      let currentRoles: string[] = ['user'];
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (Array.isArray(data.role)) {
          currentRoles = data.role;
        } else if (data.role === 'admin') {
          currentRoles = ['admin'];
        } else if (data.role === 'team_admin') {
          currentRoles = ['team_admin'];
        }
      }

      // Remove specific role
      currentRoles = currentRoles.filter(role => role !== roleString);
      
      // If no admin roles left, add 'user' role back
      if (currentRoles.length === 0 || !currentRoles.some(role => role.startsWith('admin'))) {
        currentRoles = ['user'];
      }

      // Update role array in users collection
      await setDoc(userDoc, { role: currentRoles }, { merge: true });
      
      logger.log(`Specific role ${roleString} revoked from ${email}`, { newRoles: currentRoles });
      return true;
    } catch (error) {
      logger.error('Error revoking specific role:', error);
      return false;
    }
  };

  const getAllRoles = async (): Promise<UserPermissions[]> => {
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      const roles: UserPermissions[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Handle role array migration
        let userRoles: string[] = [];
        if (Array.isArray(data.role)) {
          userRoles = data.role;
        } else if (data.role === 'admin') {
          userRoles = ['admin'];
        } else if (data.role === 'team_admin') {
          userRoles = ['team_admin'];
        } else {
          userRoles = ['user'];
        }
        
        // Only include users with admin roles
        const hasAdminRole = userRoles.some(role => 
          role === 'admin' || 
          role.startsWith('admin_org_') || 
          role.startsWith('admin_team_') ||
          role === 'team_admin'
        );
        
        if (hasAdminRole) {
          // Determine highest role for backward compatibility
          let mappedRole: UserRole = 'general_user';
          if (userRoles.includes('admin')) {
            mappedRole = 'admin';
          } else if (userRoles.some(role => role.startsWith('admin_team_') || role.startsWith('admin_org_') || role === 'team_admin')) {
            mappedRole = 'team_admin';
          }
          
          const userPermission: UserPermissions = {
            id: doc.id,
            email: data.email || '',
            role: mappedRole,
            roles: userRoles,
            assignedBy: 'system',
            assignedAt: new Date(),
            isActive: true,
            teamIds: [],
            clubIds: []
          };
          roles.push(userPermission);
        }
      });
      
      return roles;
    } catch (error) {
      logger.error('Error fetching all roles:', error);
      return [];
    }
  };

  const value: RoleContextType = {
    userRole,
    userPermissions,
    loading,
    hasRole,
    hasMinimumRole,
    isWebsiteAdmin,
    canManageOrganization,
    canManageTeam,
    getManagedOrganizations,
    getManagedTeams,
    refreshUserRole,
    assignRole,
    assignSpecificRole,
    revokeRole,
    revokeSpecificRole,
    getAllRoles
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};
